<template>
  <div class="paper-analysis-detail">
    <ots-main-title :mainTopBtn="mainTopBtnList"></ots-main-title>

    <!-- 任务信息 -->
    <div class="task-info" v-loading="loading">
      <div class="info-row">
        <span>考试课程：{{ courseResult.courseName }}</span>
        <span style="margin-left: 24px;">考试批次：{{ courseResult.planNames }}</span>
        <el-tag style="margin-left: 24px;" size="small" :type="String(courseResult.paperForm) === '1' ? '' : 'warning'">
          试卷：《{{ courseResult.paperName }}》 - {{ String(courseResult.paperForm) === '1' ? '固定卷' : '随机卷' }}
        </el-tag>
      </div>

      <!-- 指标卡片 -->
      <div class="course-section"> 
        <TaskResultCards :data="courseResult" />
      </div>

      <!-- 固定卷图表 -->
      <template v-if="String(courseResult.paperForm) === '1'">
        <div class="chart-grid">
          <div class="chart-section">
            <h3>试卷难度结构</h3>
            <div class="chart-description">说明：难度判定基于正确率：容易≥80% | 中等偏易 65-80% | 中等 50-65% | 中等偏难 35-50% | 难&lt;35%</div>
            <div id="difficultyChart" class="chart-container"></div>
          </div>
          <div class="chart-section">
            <h3>知识点正确率分布</h3>
            <div id="knowledgeChart" class="chart-container"></div>
          </div>
          <div class="chart-section">
            <h3>题型维度正确率对比</h3>
            <div id="typeChart" class="chart-container"></div>
          </div>
          <div class="chart-section">
            <div class="chart-header">
              <h3>逐题难度分析（四象限图）</h3>
              <div class="chart-header-desc">
                <span>（气泡大小=分值，悬停查看详情）</span>
                <span>X轴：正确率（难度）　Y轴：区分度</span>
              </div>
            </div>
            <div id="quadrantChart" class="chart-container"></div>
          </div>
        </div>
      </template>

      <!-- 随机卷图表 -->
      <template v-if="String(courseResult.paperForm) === '2'">
        <div class="chart-grid">
          <div class="chart-section">
            <h3>题目被抽取次数分布</h3>
            <div id="extractChart" class="chart-container"></div>
          </div>
          <div class="chart-section">
            <h3>正确率 vs 被抽次数</h3>
            <div id="correctVsExtractChart" class="chart-container"></div>
          </div>
          <div class="chart-section">
            <h3>知识点正确率分布</h3>
            <div id="knowledgeChart" class="chart-container"></div>
          </div>
          <div class="chart-section">
            <h3>各试卷难度分布</h3>
            <div id="paperDiffChart" class="chart-container"></div>
          </div>
        </div>
      </template>

      <!-- 明细数据 -->
      <div class="detail-section">
        <div style="border: 1px solid  #ebeef5; background-color: #fff; padding: 20px;">
          <h3>明细数据</h3>
          <QuestionTable :resultId="resultId" :paperForm="courseResult.paperForm" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCourseDetailAPI, getFixedPaperChartsAPI, getRandomPaperChartsAPI, exportPdfAPI } from '@/http/modules/examSet/paperAnalysis'
import TaskResultCards from './components/TaskResultCards'
import QuestionTable from './components/QuestionTable'
import { downloadAnalysisExcel, downloadAnalysisPdf } from '@/http/modules/examSet/paperAnalysisDownload'

export default {
  name: 'PaperAnalysisDetail',
  components: { TaskResultCards, QuestionTable },
  data() {
    return {
      loading: false,
      mainTopBtnList: [
        { title: '返回', type: 'info', plain: true, fn: this.goBack },
        { title: '导出PDF报告', fn: this.handleExportPdf }
      ],
      resultId: '',
      courseResult: {},
      chartData: null
    }
  },
  created() {
    this.resultId = this.$route.query.resultId
    this.loadData()
  },
  methods: {
    async loadData() {
      if (!this.resultId) return
      this.loading = true
      try {
        const res = await getCourseDetailAPI({ resultId: this.resultId })
        if (res.code === 1 && res.data) {
          this.courseResult = res.data
          // 加载图表数据
          if (res.data.paperForm === 1) {
            this.loadFixedCharts()
          } else {
            this.loadRandomCharts()
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async loadFixedCharts() {
      try {
        const res = await getFixedPaperChartsAPI({ resultId: this.resultId })
        if (res.code === 1 && res.data) {
          this.chartData = res.data
          this.$nextTick(() => {
            this.renderDifficultyChart(res.data.difficultyDistribution)
            this.renderKnowledgeChart(res.data.knowledgeCorrectRates)
            this.renderTypeChart(res.data.typeCorrectRates)
            this.renderQuadrantChart(res.data.quadrantPoints)
          })
        }
      } catch (e) {
        console.error(e)
      }
    },
    async loadRandomCharts() {
      try {
        const res = await getRandomPaperChartsAPI({ resultId: this.resultId })
        if (res.code === 1 && res.data) {
          this.chartData = res.data
          this.$nextTick(() => {
            this.renderExtractChart(res.data.extractDistributions)
            this.renderCorrectVsExtractChart(res.data.correctVsExtractPoints)
            this.renderKnowledgeChart(res.data.knowledgeCorrectRates)
            this.renderPaperDiffChart(res.data.paperDifficultyDistributions)
          })
        }
      } catch (e) {
        console.error(e)
      }
    },
    renderDifficultyChart(data) {
      if (!data || !data.length) return
      const chart = this.$echarts.init(document.getElementById('difficultyChart'))
      const levels = data.map(d => d.difficultyLevel)
      const countPercents = data.map(d => d.questionPercent)
      const scorePercents = data.map(d => d.scorePercent)
      chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['题目数量占比', '分值占比'] },
        xAxis: { type: 'category', data: levels },
        yAxis: { type: 'value', axisLabel: { formatter: '{value}%' } },
        series: [
          { name: '题目数量占比', type: 'bar', data: countPercents },
          { name: '分值占比', type: 'bar', data: scorePercents }
        ]
      })
    },
    renderKnowledgeChart(data) {
      if (!data || !data.length) return
      const chart = this.$echarts.init(document.getElementById('knowledgeChart'))
      chart.setOption({
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: data.map(d => d.knowledgePoint), axisLabel: { rotate: 30 } },
        yAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
        series: [{ type: 'bar', data: data.map(d => d.correctRate), itemStyle: { color: '#409EFF' } }]
      })
    },
    renderTypeChart(data) {
      if (!data || !data.length) return
      const chart = this.$echarts.init(document.getElementById('typeChart'))
      chart.setOption({
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
        yAxis: { type: 'category', data: data.map(d => d.questionType) },
        series: [{ type: 'bar', data: data.map(d => d.correctRate), itemStyle: { color: '#67C23A' } }]
      })
    },
    getQuadrantInfo(correctRate, discrimination) {
      const rate = Number(correctRate) || 0
      const disc = Number(discrimination) || 0
      if (rate < 50 && disc >= 0.3) {
        return { name: '左上：优质难题', color: '#409EFF' }
      }
      if (rate >= 50 && disc >= 0.3) {
        return { name: '右上：优质容易题', color: '#67C23A' }
      }
      if (rate < 50 && disc < 0.3) {
        return { name: '左下：需关注难题', color: '#F56C6C' }
      }
      return { name: '右下：送分题', color: '#909399' }
    },
    renderQuadrantChart(data) {
      if (!data || !data.length) return
      const chart = this.$echarts.init(document.getElementById('quadrantChart'))
      const seriesMap = {
        '左上：优质难题': [],
        '右上：优质容易题': [],
        '左下：需关注难题': [],
        '右下：送分题': []
      }
      data.forEach(d => {
        const quadrant = this.getQuadrantInfo(d.correctRate, d.discrimination)
        seriesMap[quadrant.name].push({
          value: [d.correctRate, d.discrimination, d.fullScore, d.questionNo],
          itemStyle: { color: quadrant.color }
        })
      })
      chart.setOption({
        tooltip: {
          formatter: function(params) {
            const d = params.data.value
            return `${params.seriesName}<br/>题号: ${d[3]}<br/>正确率: ${d[0]}%<br/>区分度: ${d[1]}<br/>分值: ${d[2]}`
          }
        },
        legend: {
          bottom: 0,
          data: Object.keys(seriesMap)
        },
        grid: { bottom: 58 },
        xAxis: {
          type: 'value', min: 0, max: 100, name: '正确率(%)',
          splitLine: { show: true }
        },
        yAxis: {
          type: 'value', min: -0.1, max: 0.7, name: '区分度',
          splitLine: { show: true }
        },
        series: Object.keys(seriesMap).map((name, index) => ({
          name,
          type: 'scatter',
          symbolSize: function(data) {
            return Math.max(data[2] * 3, 8) * 0.3
          },
          data: seriesMap[name],
          markLine: index === 0 ? {
            silent: true,
            symbol: 'none',
            lineStyle: { type: 'dashed', color: '#C0C4CC', width: 1 },
            label: { show: false },
            data: [
              { xAxis: 50 },
              { yAxis: 0.3 }
            ]
          } : undefined
        }))
      })
    },
    renderExtractChart(data) {
      if (!data || !data.length) return
      const chart = this.$echarts.init(document.getElementById('extractChart'))
      chart.setOption({
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: data.map(d => d.extractRange + '次~' + Number(d.extractRange+9) + '次'), name: '被抽取次数' },
        yAxis: { type: 'value', name: '题目数量' },
        series: [{ type: 'bar', data: data.map(d => d.questionCount), itemStyle: { color: '#409EFF' } }]
      })
    },
    renderCorrectVsExtractChart(data) {
      if (!data || !data.length) return
      const chart = this.$echarts.init(document.getElementById('correctVsExtractChart'))
      chart.setOption({
        tooltip: {
          formatter: function(params) {
            const d = params.data
            return `题号: ${d[2]}<br/>被抽次数: ${d[0]}<br/>正确率: ${d[1]}%`
          }
        },
        xAxis: { type: 'value', name: '被抽次数' },
        yAxis: { type: 'value', max: 100, name: '正确率(%)' },
        series: [{
          type: 'scatter',
          data: data.map(d => [d.extractCount, d.correctRate, d.questionNo]),
          itemStyle: { color: '#E6A23C' }
        }]
      })
    },
    renderPaperDiffChart(data) {
      if (!data || !data.length) return
      const chart = this.$echarts.init(document.getElementById('paperDiffChart'))
      chart.setOption({
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: data.map(d => d.difficultyRange), name: '难度系数' },
        yAxis: { type: 'value', name: '试卷份数' },
        series: [{ type: 'bar', data: data.map(d => d.paperCount), itemStyle: { color: '#67C23A' } }]
      })
    },
    goBack() {
      const taskId = this.$route.query.taskId
      if (taskId) {
        this.$router.push({ name: 'paperAnalysisResult', query: { taskId } })
      } else {
        this.$router.go(-1)
      }
    },
    handleExportPdf() {
      // this.$message.info('PDF导出功能开发中')
      downloadAnalysisPdf(this.resultId,  this.courseName) 
    },

    handleExportExcel() { 
      downloadAnalysisExcel(this.resultId, this.courseName) 
    },

  }
}
</script>

<style lang="less" scoped>
.paper-analysis-detail { padding:0px 20px; }
.task-info {
  .info-row { margin-bottom: 16px; color: #606266; font-size: 14px; }
}
.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 24px;
}
.chart-section {
  margin-top: 0;
  padding: 16px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  h3 { font-size: 16px; color: #303133; margin-bottom: 12px; }
  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
    h3 { margin-bottom: 0; }
  }
  .chart-header-desc {
    display: flex;
    gap: 24px;
    color: #909399;
    font-size: 12px;
    line-height: 20px;
  }
  .chart-description { color: #909399; font-size: 12px; line-height: 20px; margin-bottom: 8px; }
  .chart-container { width: 100%; height: 350px; }
  .quadrant-legend {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px 24px;
    color: #909399;
    font-size: 12px;
    line-height: 20px;
    margin-top: 8px;
  }
  .quadrant-legend-item {
    position: relative;
    padding-left: 14px;
  }
  .quadrant-legend-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 7px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .quadrant-top-left::before { background: #409EFF; }
  .quadrant-top-right::before { background: #67C23A; }
  .quadrant-bottom-left::before { background: #F56C6C; }
  .quadrant-bottom-right::before { background: #909399; }
}
.detail-section {
  margin-top: 24px;
  h3 { font-size: 16px; color: #303133; margin-bottom: 12px; }
}
.course-section {
  margin-bottom: 24px;
  padding: 20px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px; 
}
</style>
